import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { dayjs } from '@karve.it/core';
import { CalendarEventService } from '@karve.it/features';
import { CalendarEvent, GenerateCalendarEventsQueryInput, ListCalendarEventsOutput } from '@karve.it/interfaces/calendarEvents';
import { Job, ListJobsFunctionOutput } from '@karve.it/interfaces/jobs';
import { Store } from '@ngrx/store';
import {QueryRef} from 'apollo-angular';

import { cloneDeep } from 'lodash';
import { Subscription } from 'rxjs';
import { ArtifactsComponent } from 'src/app/artifacts/artifacts.component';
import { CommentsComponent } from 'src/app/comments/comments.component';
import { JOB_ROLE_MAP, pagination } from 'src/app/global.constants';
import { JobsComponent } from 'src/app/jobs/jobs.component';
import { strToTitleCase } from 'src/app/js';
import { LocationsComponent } from 'src/app/locations/locations.component';
import { DetailsHelperService } from 'src/app/services/details-helper.service';
import { PageTitleService } from 'src/app/services/page-title.service';
import { PermissionService } from 'src/app/services/permission.service';
import { RecentItemsService } from 'src/app/services/recent-items.service';

import { MutateUserComponent } from 'src/app/shared/mutate-user/mutate-user.component';
import { SubSink } from 'subsink';

import { FullUserWithZoneParentFragment, ListFieldsGQL, ListFieldsQueryVariables, ListHistoryFilterInput, SetFieldValuesGQL, UserPageUserGQL, UserPageUserQuery, UserPageUserQueryVariables } from '../../../generated/graphql.generated';
import { FreyaMutateService } from '../../services/freya-mutate.service';

import { UserPageSimilarCustomersActions } from './user-page-similar-customers/user-page-similar-customers.actions';

@Component({
  selector: 'app-user-page',
  templateUrl: './user-page.component.html',
  styleUrls: ['./user-page.component.scss']
})
export class UserPageComponent implements OnInit, OnDestroy {

  @ViewChild('mutate') mutateRef: MutateUserComponent;

  // Component Refs
  @ViewChild('jobs', { static: false }) jobsRef: JobsComponent;
  @ViewChild('locations', { static: false }) locationsRef: LocationsComponent;
  @ViewChild('comments', { static: false }) commentsRef: CommentsComponent;
  @ViewChild('artifacts', { static: false }) artifactsRef: ArtifactsComponent;

  pageItemPagination = cloneDeep(pagination);

  subs = new SubSink();

  permissions = {
    createUser: false,
    editUser: false,
  };

  // User Variables
  user: FullUserWithZoneParentFragment;
  userLoaded = false;
  activeTabIndex = 0;

  tabs = ['overview', 'jobs', 'locations', 'payments', 'documents', 'notifications', 'similar-customers'];
  hasIndexLoaded = [false, false, false, false, false, false, false];

  // Job Variables
  jobs: Job[];
  jobTotal = 0;
  jobsLoading = true;

  // Event Variables
  events: CalendarEvent[];
  eventsTotal = 0;
  eventsLoading = true;

  hasScrolled = false;

  jobConstants = JOB_ROLE_MAP;

  // Query Refs
  userQueryRef: QueryRef<UserPageUserQuery, UserPageUserQueryVariables>;
  userQuerySubscription?: Subscription;
  jobsQueryRef: QueryRef<ListJobsFunctionOutput>;
  eventsQueryRef: QueryRef<ListCalendarEventsOutput>;

  // Notifications
  notificationHistoryFilter: ListHistoryFilterInput = {
    types: ['sent sms', 'sent email', 'sent push notification', 'sent local notification'],
  };

  smsOptIn;
  autoEmailsPreference;

  notificationsPreferencesWatchQuery: ReturnType<typeof this.listFieldsGQL.watch>;
  notificationsPreferencesSub: Subscription;

  constructor(
    private route: ActivatedRoute,
    private userPageUserGQL: UserPageUserGQL,
    private eventService: CalendarEventService,
    public detailsHelperService: DetailsHelperService,
    private angularLocation: Location,
    private router: Router,
    private permissionHandler: PermissionService,
    private titleService: PageTitleService,
    private recentItems: RecentItemsService,
    private freyaMutate: FreyaMutateService,
    private setFieldValuesGQL: SetFieldValuesGQL,
    private listFieldsGQL: ListFieldsGQL,
    private store: Store,
  ) {
    this.subs.sink = this.route.params.subscribe(params => {
      this.userLoaded = false;
      this.retrieveUser(params.id);
      this.user = { id: params.id };
      if (params.tab) {
        this.changeTabByIndex(this.tabs.indexOf(params.tab));
      }
    });
  }

  ngOnInit(): void {
    this.pageItemPagination.defaultNumberOfItems = 5;
    this.pageItemPagination = cloneDeep(this.pageItemPagination);

    const pagePermissions = ['users.create', 'users.edit'];

    this.subs.sink = this.permissionHandler.watchPermissions(pagePermissions).subscribe((res) => {
      this.permissions.createUser = res[0];
      this.permissions.editUser = res[1];
    });

    this.subs.sink = this.detailsHelperService.getObjectUpdates('User').subscribe(() => {
      this.userQueryRef.resetLastResults();
      this.userQueryRef.refetch();
    });
  }

  ngOnDestroy() {
    this.watchSMSOptIn(false);
    this.subs?.unsubscribe();
  }

  retrieveUser(id: string) {
    this.userQuerySubscription?.unsubscribe();
    this.userQueryRef = this.userPageUserGQL.watch({ filter: { userIds: [id], showDeactivated: true } });

    this.userQuerySubscription = this.userQueryRef.valueChanges.subscribe((res) => {
      if (res.data.usersv2.users?.length === 0) {
        this.router.navigate(['/notfound']);
        return;
      }

      if (res.networkStatus === 7) {
        this.user = res.data.usersv2.users.find((u) => u.id === id);
        this.userLoaded = true;
        this.recentItems.addToRecentItems(this.user, 'users');
        this.updatePageTitle();
        this.watchSMSOptIn(true);

        if (this.commentsRef) {
          this.initComments();
        } else {
          setTimeout(() => {
            this.initComments();
          }, 1000);
        }

        if (this.activeTabIndex === this.tabs.indexOf('similar-customers')) {
          this.dispatchSimilarCustomersTabOpened();
        }
      }
    });

    this.subs.sink = this.userQuerySubscription;
  }

  updatePageTitle() {
    if ( !this.user ) { return; };
    const tabName = strToTitleCase(this.tabs[this.activeTabIndex]);
    const title = `${this.user.givenName} ${this.user.familyName}: ${tabName}`;

    this.titleService.setCustomPageTitle({
      pageTitle: title,
    });
  }

  initComments() {
    this.commentsRef.commentsFilter = { objectIds: [this.user.id] };
    this.commentsRef.objectId = this.user.id;
    this.commentsRef.fetchComments();
  }

  retrieveEvents(userId: string) {
    const eventQueryInput = {} as GenerateCalendarEventsQueryInput;

    this.eventsQueryRef = this.eventService.watchCalendarEvents(
      {
        filter: {
          users: [userId],
          min: Math.floor(dayjs(new Date()).valueOf() / 1000),
        },
        limit: 3,
      }, eventQueryInput);

    this.subs.sink = this.eventsQueryRef.valueChanges.subscribe((res) => {
      if (res.networkStatus === 7) {
        this.events = res.data.calendarEvents.events;
        this.eventsTotal = res.data.calendarEvents.total;
        this.eventsLoading = false;
      }
    });
  }

  changeTabByIndex(index: number) {
    this.activeTabIndex = index;
    this.angularLocation.go(`user/${this.user.id}/${this.tabs[index]}`);
    this.updatePageTitle();

    this.hasIndexLoaded[index] = true;

    if (index === 0) {
      this.retrieveEvents(this.user.id);
    }

    // On a direct link to this tab, the user query hasn't resolved yet — `retrieveUser`
    // dispatches once it has, so this only needs to cover the tab-click path.
    if (index === this.tabs.indexOf('similar-customers') && this.userLoaded) {
      this.dispatchSimilarCustomersTabOpened();
    }
  }

  dispatchSimilarCustomersTabOpened() {
    this.store.dispatch(UserPageSimilarCustomersActions.tabOpened({
      user: {
        id: this.user.id,
        roleIds: this.user.roles?.map((role) => role.id) ?? [],
        roles: this.user.roles,
        zones: this.user.zones,
        givenName: this.user.givenName,
        familyName: this.user.familyName,
        email: this.user.email,
        phone: this.user.phone,
        company: this.user.company,
        billingAddress: this.user.billingAddress,
        metadata: this.user.metadata,
      },
    }));
  }

  get isCustomer(): boolean {
    return !!this.user?.roles?.length && this.user.roles.every((role) => role.name.includes('Customer'));
  }

  openEditUser() {
    this.freyaMutate.openMutateObject({
      mutateType: 'update',
      objectType: 'user',
      object: this.user,
    });
  }

  openEditTabItem(item: string) {
    if (item === 'location') {
      this.changeTabByIndex(2);
      if (this.hasLazyLoaded(this.locationsRef, 'location')) {
        this.locationsRef.openMutateDialog();
      };
    } else if (item === 'comment') {
      this.commentsRef.openMutateComment('create');
    }
  }

  hasLazyLoaded(ref, item: string) {
    if (!ref) {
      setTimeout(() => {
        this.openEditTabItem(item);
      }, 250);
    }

    return ref as boolean;
  }

  /***
   * Notification Options
   */

  updateNotificationsPreferences(notification: string, preference: boolean) {
    this.setFieldValuesGQL.mutate({
      objects: [this.user.id],
      objectLabel: 'User',
      fields: [{
        fieldName: notification,
        value: preference,
      }]
    }, {
      refetchQueries: ['listFields'],
    }).subscribe({
      next: (_res) => {
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  setPreferencesVariables() {
    return this.notificationsPreferencesWatchQuery.setVariables(this.getPreferencesVariables());
  }

  watchSMSOptIn(enable?: boolean) {
    enable = enable === undefined ? !this.notificationsPreferencesWatchQuery : enable;

    if (!enable) {
      this.notificationsPreferencesSub?.unsubscribe();
      delete this.notificationsPreferencesWatchQuery;
      this.autoEmailsPreference = undefined;
      this.smsOptIn = undefined;

      return;
    }

    if (enable && this.notificationsPreferencesWatchQuery) {
      this.setPreferencesVariables();
      return;
    }

    this.notificationsPreferencesWatchQuery = this.listFieldsGQL.watch(this.getPreferencesVariables(), {
      fetchPolicy: 'cache-and-network',
    });

    this.subs.sink = this.notificationsPreferencesSub =
      this.notificationsPreferencesWatchQuery.valueChanges.subscribe((res) => {

      const fields = res?.data?.fields?.fields || [];

      const smsField = fields?.find(field => field.name === 'notifications.sms');
      const autoEmailsField = fields?.find(field =>
        field.name === 'notifications.emailJobTrigger-email');

      const [smsValue] = smsField?.values || [];
      const [autoEmailsValue] = autoEmailsField?.values || [];

      this.smsOptIn = smsValue?.value;
      this.autoEmailsPreference = autoEmailsValue?.value;
    });
  }

  getPreferencesVariables(): ListFieldsQueryVariables {
    return {
      objectLabels: ['User'],
      objects: [ this.user.id ],
      filter: {
        names: [
          'notifications.sms',
          'notifications.emailJobTrigger-email'
        ],
      }
    };
  }
}
